/**
 * PortalPageRenderer — Dynamic page renderer for portal pages
 *
 * Renders a portal page using its widget grid layout.
 * Each widget is resolved from either:
 *   - Widget registry (type: "registry") for data widgets
 *   - Inline config (type: "text") for text/content widgets
 *
 * Supports:
 *   - Responsive grid layout (react-grid-layout)
 *   - Widget editability control per widget
 *   - Portal-specific translations
 *   - Breadcrumbs from portal template
 *   - CASL-gated widget visibility
 */

import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Responsive, WidthProvider } from "react-grid-layout";
import {
  Box,
  Card,
  Breadcrumbs,
  IconButton,
  Tooltip,
  CircularProgress,
  alpha,
} from "@mui/material";
import {
  NavigateNext as BreadcrumbSeparator,
  Fullscreen as ExpandIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import KDTypography from "components/KDTypography";
import KDBox from "components/KDBox";
import WidgetRenderer from "layouts/dashboard/components/WidgetRenderer";
import { useMaterialUIController } from "context";
import { useAbility } from "context/AbilityContext";
import { usePortalContext } from "context/PortalContext";
import logger from "services/logger";

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const ResponsiveGridLayout = WidthProvider(Responsive);

/**
 * ExpandedWidgetModal — Full-screen widget display
 */
function ExpandedWidgetModal({ widgetKey, widgetConfig, language, darkMode, onClose }) {
  if (!widgetKey) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        bgcolor: darkMode ? "rgba(0,0,0,0.9)" : "rgba(255,255,255,0.95)",
        display: "flex",
        flexDirection: "column",
        p: 3,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <IconButton onClick={onClose} sx={{ color: darkMode ? "white" : "rgba(0,0,0,0.7)" }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <WidgetRenderer
          widgetId={widgetKey}
          isExpanded
          portalWidgetConfig={widgetConfig}
          language={language}
        />
      </Box>
    </Box>
  );
}

/**
 * PortalWidgetWrapper — Wraps each widget on a portal page
 */
function PortalWidgetWrapper({
  widgetKey,
  widgetConfig,
  language,
  darkMode,
  userEditable,
  onExpand,
  onContentChange,
}) {
  const isText = widgetConfig?.type === "text";

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: darkMode ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
        backdropFilter: darkMode ? "blur(10px)" : "none",
        border: darkMode
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.08)",
        borderRadius: "16px",
        overflow: "hidden",
        transition: "all 0.3s ease",
        boxShadow: darkMode ? "none" : "0 1px 3px rgba(0,0,0,0.08)",
        "&:hover .portal-widget-expand": {
          opacity: 1,
        },
      }}
    >
      {/* Expand button (top-right, hidden until hover) */}
      {!isText && (
        <Tooltip title="Expand">
          <IconButton
            className="portal-widget-expand"
            size="small"
            onClick={() => onExpand(widgetKey)}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              opacity: 0,
              transition: "opacity 0.2s ease",
              zIndex: 2,
              color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)",
              bgcolor: darkMode ? alpha("#fff", 0.05) : alpha("#000", 0.05),
              "&:hover": {
                bgcolor: darkMode ? alpha("#fff", 0.1) : alpha("#000", 0.1),
              },
            }}
          >
            <ExpandIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
        <WidgetRenderer
          widgetId={widgetKey}
          portalWidgetConfig={widgetConfig}
          language={language}
          userEditable={userEditable}
          onContentChange={
            userEditable
              ? (newContent, lang) => onContentChange(widgetKey, newContent, lang)
              : undefined
          }
        />
      </Box>
    </Card>
  );
}

/**
 * PortalPageRenderer — Main component
 */
function PortalPageRenderer({ pageId, pageSlug }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const ability = useAbility();
  const portalCtx = usePortalContext();
  const { activePortal, getBreadcrumbs, getDefaultLanguage } = portalCtx;

  const [expandedWidget, setExpandedWidget] = useState(null);

  // Resolve the page
  const page = useMemo(() => {
    if (pageId) return portalCtx.getPage(pageId);
    if (pageSlug) return portalCtx.getPageBySlug(pageSlug);
    return null;
  }, [pageId, pageSlug, portalCtx]);

  const language = getDefaultLanguage();
  const breadcrumbs = getBreadcrumbs(page?.name || "");

  // Handle content change for user-editable widgets
  const handleContentChange = useCallback(
    (widgetKey, newContent, lang) => {
      logger.debug("[PortalPage]", `Widget "${widgetKey}" content changed (${lang}):`, newContent);
      // Content changes for user-editable widgets would be persisted
      // via the portal API (save user-specific overrides)
    },
    []
  );

  // Filter widgets by CASL permissions
  const visibleWidgets = useMemo(() => {
    if (!page || !page.widgets) return {};
    const filtered = {};
    Object.entries(page.widgets).forEach(([key, config]) => {
      // Check CASL if widget specifies permission requirements
      if (config.caslAction && config.caslSubject) {
        if (!ability.can(config.caslAction, config.caslSubject)) return;
      }
      filtered[key] = config;
    });
    return filtered;
  }, [page, ability]);

  // Build layout from page config, only including visible widgets
  const layout = useMemo(() => {
    if (!page || !page.layout) return [];
    return page.layout.filter((item) => visibleWidgets[item.i]);
  }, [page, visibleWidgets]);

  if (!activePortal) {
    return (
      <KDBox display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress size={40} />
      </KDBox>
    );
  }

  if (!page) {
    return (
      <KDBox p={3}>
        <KDTypography variant="h5" color={darkMode ? "white" : "dark"}>
          Page not found
        </KDTypography>
        <KDTypography variant="body2" sx={{ color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)", mt: 1 }}>
          The requested portal page could not be found.
        </KDTypography>
      </KDBox>
    );
  }

  return (
    <KDBox p={3}>
      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<BreadcrumbSeparator fontSize="small" />}
          sx={{ mb: 2, color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)" }}
        >
          {breadcrumbs.map((crumb, idx) => (
            <KDTypography
              key={idx}
              variant="body2"
              sx={{
                color: idx === breadcrumbs.length - 1
                  ? (darkMode ? "white" : "rgba(0,0,0,0.87)")
                  : (darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)"),
                fontWeight: idx === breadcrumbs.length - 1 ? 600 : 400,
              }}
            >
              {crumb.label}
            </KDTypography>
          ))}
        </Breadcrumbs>
      )}

      {/* Widget Grid */}
      <ResponsiveGridLayout
        className="portal-page-layout"
        layouts={{ lg: layout }}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
        cols={{ lg: 12, md: 10, sm: 6, xs: 4 }}
        rowHeight={60}
        isDraggable={false}
        isResizable={false}
        containerPadding={[0, 0]}
        margin={[16, 16]}
      >
        {layout.map((item) => {
          const widgetConfig = visibleWidgets[item.i];
          const isEditable = widgetConfig?.userEditable === true;

          return (
            <div key={item.i} data-grid={item}>
              <PortalWidgetWrapper
                widgetKey={item.i}
                widgetConfig={widgetConfig}
                language={language}
                darkMode={darkMode}
                userEditable={isEditable}
                onExpand={setExpandedWidget}
                onContentChange={handleContentChange}
              />
            </div>
          );
        })}
      </ResponsiveGridLayout>

      {/* Expanded widget modal */}
      {expandedWidget && (
        <ExpandedWidgetModal
          widgetKey={expandedWidget}
          widgetConfig={visibleWidgets[expandedWidget]}
          language={language}
          darkMode={darkMode}
          onClose={() => setExpandedWidget(null)}
        />
      )}
    </KDBox>
  );
}

PortalPageRenderer.propTypes = {
  pageId: PropTypes.string,
  pageSlug: PropTypes.string,
};

export default PortalPageRenderer;
